import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, LifeBuoy, Plus, MessageSquare, CheckCircle2, Clock, Youtube, Facebook, Music2, Link2, Users, ListMusic,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { EmptyState, fmtDate } from "@/components/dashboard/ui";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";
const labelCls = "text-[11px] uppercase tracking-widest text-slate-500 mb-1 block";

const CATEGORY_ICONS = {
  youtube_cid_whitelist: Youtube,
  meta_profile_linking:  Facebook,
  vevo_channel_request:  Music2,
  mapping_error:         Link2,
  oac_link_request:      Users,
  playlist_pitching:     ListMusic,
  other:                 MessageSquare,
};

const STATUS_PILL = {
  open:        { label: "Open",         cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  in_progress: { label: "In progress",  cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  resolved:    { label: "Resolved",     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  closed:      { label: "Closed",       cls: "bg-white/5 text-slate-400 border-white/10" },
};

function StatusChip({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.open;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border ${s.cls}`}>
      {status === "resolved" ? <CheckCircle2 size={11} /> : <Clock size={11} />}
      {s.label}
    </span>
  );
}

export default function ArtistTickets() {
  const [tickets, setTickets] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "", title: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/tickets"),
        api.get("/tickets/categories"),
      ]);
      setTickets(t.data);
      setCats(c.data);
    } catch (e) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Pick a category");
    if (!form.title.trim()) return toast.error("Add a short subject");
    if (form.description.trim().length < 10) return toast.error("Describe your request in a few sentences");
    setSaving(true);
    try {
      await api.post("/tickets", form);
      toast.success("Ticket submitted. We'll email you when we reply.");
      setOpen(false);
      setForm({ category: "", title: "", description: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't submit ticket"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="artist-tickets">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Support Tickets</h2>
          <p className="mt-1.5 text-slate-400">
            Request help with YouTube Content ID, VEVO channels, store mapping and playlist pitching. We reply by email.
          </p>
        </div>
        <button
          data-testid="new-ticket-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 hover:bg-slate-200 active:scale-[0.98] transition-all shrink-0"
        >
          <Plus size={16} /> New ticket
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {cats.filter((c) => c.key !== "other").map((c) => {
          const Icon = CATEGORY_ICONS[c.key] || MessageSquare;
          return (
            <button
              key={c.key}
              data-testid={`ticket-quick-${c.key}`}
              onClick={() => { setForm({ category: c.key, title: c.label, description: "" }); setOpen(true); }}
              className="text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] p-4 transition-colors"
            >
              <Icon size={18} className="text-blue-400" />
              <div className="mt-3 text-sm text-white font-medium leading-snug">{c.label}</div>
              <div className="mt-1 text-[11px] text-slate-500">Tap to start a ticket</div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : tickets.length === 0 ? (
        <EmptyState title="No tickets yet" desc="Pick a category above or click 'New ticket' to start." />
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.ticket_id} data-testid={`ticket-row-${t.ticket_id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-widest text-slate-500">{t.category_label}</span>
                    <StatusChip status={t.status} />
                  </div>
                  <div className="mt-1 text-white font-medium">{t.title}</div>
                  <p className="mt-1.5 text-sm text-slate-400 line-clamp-2">{t.description}</p>
                  {t.admin_reply && (
                    <div className="mt-3 rounded-xl border border-blue-500/20 bg-blue-500/5 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-blue-300 mb-1">Noizbloc reply</div>
                      <p className="text-sm text-slate-200 whitespace-pre-wrap">{t.admin_reply}</p>
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-500 shrink-0 text-right">
                  <div>{fmtDate(t.created_at)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-lg" data-testid="new-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <LifeBuoy size={20} className="text-blue-400" /> New support ticket
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Give us the details — we&apos;ll reply to your registered email within 1-2 business days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="new-ticket-form">
            <div>
              <label className={labelCls}>Category</label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="ticket-category-select" className="bg-white/5 border-white/10 text-white h-[44px] rounded-xl text-sm">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                  {cats.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Subject</label>
              <input
                data-testid="ticket-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className={inputCls}
                placeholder="Short summary of your request"
              />
            </div>
            <div>
              <label className={labelCls}>Details</label>
              <textarea
                data-testid="ticket-description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className={inputCls + " min-h-[140px]"}
                placeholder="Include as much context as possible — links, UPC/ISRC, artist channel URL, screenshots links, etc."
              />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-white/15 text-white text-sm px-5 py-2.5 hover:bg-white/5">Cancel</button>
              <button
                data-testid="ticket-submit"
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium text-sm px-6 py-2.5 hover:bg-slate-200 disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : "Submit ticket"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
